<template>
  <div
    v-if="!inlineLabel && isTextField"
    class="full-width row"
  >
    <div class="col-12 text-h6 text-left">
      {{ sessionNoteField.label }}
    </div>
    <div
      v-if="sessionNoteField.hint"
      class="col-12 text-subtitle1 text-left q-py-md"
    >
      {{ sessionNoteField.hint }}
    </div>
    <voice-input
      :model-value="sessionNoteField.value"
      :rules="rules.inputValidationRules"
      :type="sessionNoteField.type === 'number' ? 'number' : 'text'"
      :disable-voice-input="disableVoiceInput"
      :show-refresh="isAITextField && !disableAIRefresh"
      data-e2e="input-ai-generated-text"
      outlined
      autofocus
      :loading="isGeneratingText"
      :autogrow="allowMultiLines"
      hide-hint
      :disable="disabledFields || isGeneratingText"
      class="col-12"
      @keyup.enter="onContinueFromTextField"
      @processing="onProcessing"
      @refresh="onRefreshAIField"
      @update:model-value="onInputValue"
    />
  </div>
  <q-input
    v-else-if="inlineLabel && isTextField"
    :model-value="sessionNoteField.value"
    :rules="rules.inputValidationRules"
    :type="sessionNoteField.type === 'number' ? 'number' : 'text'"
    :label="sessionNoteField.label"
    :hint="sessionNoteField.hint"
    :loading="isGeneratingText"
    outlined
    autofocus
    :autogrow="allowMultiLines"
    hide-hint
    :disable="disabledFields || isGeneratingText"
    class="col-12"
    @update:model-value="onInputValue"
  />
  <date-input
    v-else-if="isDateField"
    :model-value="convertTimestamp(sessionNoteField.value)"
    :label="sessionNoteField.label"
    outlined
    preserve-time
    :disable="disabledFields"
    @update:model-value="onInputDate"
  />
  <time-input
    v-else-if="sessionNoteField.type === 'time'"
    :model-value="convertTimestamp(sessionNoteField.value)"
    :label="sessionNoteField.label"
    :disable="disable || disabledFields"
    stack-label
    :hint="warning"
    outlined
    preserve-date
    :allow-empty="!$q.platform.is.safari"
    @update:model-value="onInputTime"
  >
    <template
      v-if="warning"
      #prepend
    >
      <q-icon
        name="warning"
        color="warning"
      />
    </template>
  </time-input>
  <q-input
    v-else-if="sessionNoteField.type === 'duration'"
    :model-value="sessionNoteField.value"
    :label="sessionNoteField.label"
    :disable="disabledFields"
    hint=""
    mask="#:##:##"
    fill-mask="0"
    outlined
    autofocus
    input-class="text-right"
    class="col-12 q-mb-md"
    @update:model-value="onInputValue"
  />
  <div
    v-else-if="isYesNoField"
  >
    <q-checkbox
      :model-value="sessionNoteField.value || false"
      :label="sessionNoteField.label"
      :disable="disabledFields"
      :hint="sessionNoteField.hint"
      class="text-left q-mt-md"
      @update:model-value="onInputValue"
    />
  </div>
  <div
    v-else-if="isSelectField"
  >
    <div class="col-12 text-h6 text-left">
      {{ sessionNoteField.label }}
    </div>
    <div
      v-if="sessionNoteField.hint"
      class="col-12 text-subtitle1 text-left q-pt-md"
    >
      {{ sessionNoteField.hint }}
    </div>
    <q-option-group
      v-if="allowMultipleOptions"
      type="checkbox"
      :model-value="formatSelectValue(sessionNoteField.value)"
      :options="formattedSelectOptions"
      :disable="disabledFields"
      class="text-left q-mt-md"
      @update:model-value="onSelectOption"
    />
    <q-option-group
      v-else
      type="radio"
      :model-value="sessionNoteField.value"
      :options="formattedSelectOptions"
      :disable="disabledFields"
      class="text-left q-mt-md"
      @update:model-value="onSelectRadioOption"
    />
  </div>
  <div
    v-else-if="isScoreField"
    class="full-width"
  >
    <div class="col-12 text-h6 text-left">
      {{ sessionNoteField.label }}
    </div>
    <div
      v-if="sessionNoteField.hint"
      class="col-12 text-subtitle1 text-left q-pt-md"
    >
      {{ sessionNoteField.hint }}
    </div>
    <div
      v-if="isScoreTypeLinearScale"
      class="col-12 row q-mt-md"
    >
      <q-markup-table
        v-if="$q.screen.gt.xs"
        flat
        dense
        class="col-12"
      >
        <thead>
          <tr>
            <th
              class="text-left"
              :colspan="Math.ceil(formattedScoreRangeOptions.length / 2)"
            >
              {{ sessionNoteField.scoreConfig?.scoreRange?.min?.label }}
            </th>
            <th
              class="text-right"
              :colspan="Math.floor(formattedScoreRangeOptions.length / 2)"
            >
              {{ sessionNoteField.scoreConfig?.scoreRange?.max?.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td
              v-for="scoreRangeOption in formattedScoreRangeOptions"
              :key="scoreRangeOption.value"
              class="text-center"
            >
              {{ scoreRangeOption.label }}
            </td>
          </tr>
          <tr>
            <td
              v-for="scoreRangeOption in formattedScoreRangeOptions"
              :key="scoreRangeOption.value"
              class="text-center"
            >
              <q-radio
                :model-value="sessionNoteField.value"
                :val="scoreRangeOption.value"
                :disable="disabledFields"
                @update:model-value="onSelectRadioOption"
              />
            </td>
          </tr>
        </tbody>
      </q-markup-table>
      <q-markup-table
        v-else
        flat
        dense
        class="col-12"
      >
        <thead>
          <tr />
        </thead>
        <tbody>
          <tr
            v-for="(scoreRangeOption, idx) in formattedScoreRangeOptions"
            :key="scoreRangeOption.value"
          >
            <td
              class="text-center"
            >
              <q-radio
                :model-value="sessionNoteField.value"
                :val="scoreRangeOption.value"
                :label="String(scoreRangeOption.label)"
                :disable="disabledFields"
                @update:model-value="onSelectRadioOption"
              />
            </td>
            <td
              class="text-left"
            >
              <template v-if="idx === 0 && formattedScoreRangeOptions.length > 1">
                {{ sessionNoteField.scoreConfig?.scoreRange?.min?.label }}
              </template>
              <template
                v-else-if="idx === formattedScoreRangeOptions.length -1
                  && formattedScoreRangeOptions.length > 1"
              >
                {{ sessionNoteField.scoreConfig?.scoreRange?.max?.label }}
              </template>
            </td>
          </tr>
        </tbody>
      </q-markup-table>
    </div>
    <q-option-group
      v-else-if="isScoreTypeExplicit"
      type="radio"
      :model-value="sessionNoteField.value"
      :options="formattedExplicitScoreOptions"
      map-options
      emit-value
      :disable="disabledFields"
      class="text-left q-mt-md"
      @update:model-value="onSelectRadioOption"
    />
  </div>
</template>
<script>
import { callFirebaseFunction } from 'boot/firebase';
import DateInput from 'components/DateInput';
import TimeInput from 'components/UI/TimeInput';
import { Timestamp } from 'firebase/firestore';
import {
  chain, find, get, has,
  includes, isArray, isDate, isEmpty, isFunction, isNaN, isNumber, map, size,
  split, sumBy,
} from 'lodash';
import { mapState } from 'pinia';
import VoiceInput from 'src/components/UI/VoiceInput.vue';
import { SESSION_NOTE_FIELDS } from 'src/constants/dynamicFields';
import Auth from 'src/mixins/Auth';
import ClientsMixin from 'src/mixins/ClientsMixin';
import Interactions from 'src/mixins/Interactions';
import Toolbox from 'src/mixins/Toolbox';
import { auth } from 'src/store/auth';
import { clients } from 'src/store/clients';
import { dataFields } from 'src/store/dataFields';
import { network } from 'src/store/network';
import { programs } from 'src/store/programs';
import { promptLevels } from 'src/store/promptLevels';
import AppUtils, { isValidEmail } from 'src/utils/AppUtils';
import { aiErrorMessageKey } from 'src/utils/aiErrors';
import { calculateAge } from 'src/utils/DateUtils';

export default {
  name: 'SessionNoteFieldForm',

  components: { DateInput, TimeInput, VoiceInput },

  mixins: [Auth, ClientsMixin, Interactions, Toolbox],

  props: {
    sessionNoteField: {
      type: Object,
      required: true,
    },
    groupedSessionNoteFields: {
      type: Array,
      required: false,
      default: () => ([]),
    },
    inlineLabel: {
      type: Boolean,
      default: false,
    },
    session: {
      type: Object,
      default: null,
    },
    sessionActivity: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    linkedAppointment: {
      type: Object,
      default: null,
    },
    disabledFields: {
      type: Boolean,
      default: false,
    },
    sessionData: {
      type: Object,
      default: null,
    },
    sessionDataNotes: {
      type: Object,
      default: null,
    },
    timedTargetMeasurements: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: ['input', 'select-option', 'select-single-option', 'continue', 'block-continue'],

  data() {
    return {
      isGeneratingText: false,

      rules: {
        inputValidationRules: [
          (val) => {
            if (!this.isValidField(val)) {
              if (this.isEmailClientField && !isEmpty(val)) {
                return this.$t('sessions.noteTemplate.inputEmailValidator');
              }

              return this.$t('sessions.noteTemplate.validateSessionNoteField');
            }

            return true;
          },
        ],
      },
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'isSubscriptionPremium']),
    ...mapState(network, ['offlineMode']),
    ...mapState(programs, ['programs']),
    ...mapState(promptLevels, ['promptLevelTemplates']),
    ...mapState(dataFields, ['clientDataFields']),
    ...mapState(clients, ['clients']),

    disable() {
      if (isFunction(this.sessionNoteField.isDisabled)) {
        return this.sessionNoteField.isDisabled(this.session, this.sessionActivity);
      }
      return false;
    },

    disableVoiceInput() {
      return this.sessionNoteField.type === 'number'
        || !this.userHasPrivilege('sessionActivities/allowVoiceInput')
        || this.offlineMode
        || !('mediaDevices' in navigator);
    },

    disableAIRefresh() {
      return this.sessionNoteField.type === 'number'
        || this.offlineMode;
    }, // disableAIRefresh()

    clientField() {
      const [clientDataProperty, clientDataFieldId] = split(this.sessionNoteField.clientField, '.');
      if (clientDataProperty === 'customData') {
        return find(this.clientDataFields, { id: clientDataFieldId });
      }
      return find(
        this.clientColumns,
        (clientColumn) => (
          (includes(clientColumn.profileTypes, 'client') && clientColumn.field === clientDataProperty)),
      );
    },

    isTextField() {
      return ['text', 'aiText', 'number'].includes(this.sessionNoteField.type)
        || (this.sessionNoteField.type === 'clientField' && this.clientField?.type === 'text');
    },

    isScoreField() {
      return this.sessionNoteField.type === 'score';
    },

    isScoreTypeLinearScale() {
      return this.sessionNoteField.type === 'score'
        && this.sessionNoteField.scoreConfig?.scoreType === 'linearScale';
    },

    isScoreTypeExplicit() {
      return this.sessionNoteField.type === 'score'
        && this.sessionNoteField.scoreConfig?.scoreType === 'explicit';
    },

    isAITextField() {
      return this.sessionNoteField.type === 'aiText';
    },

    isDateField() {
      return ['date'].includes(this.sessionNoteField.type)
        || (this.sessionNoteField.type === 'clientField' && this.clientField?.type === 'date');
    },

    isSelectField() {
      return (this.sessionNoteField.type === 'select')
        || (this.sessionNoteField.type === 'clientField' && this.clientField?.type === 'select');
    },

    isYesNoField() {
      return ['yesNo'].includes(this.sessionNoteField.type)
        || (this.sessionNoteField.type === 'clientField' && this.clientField?.type === 'yesNo');
    },

    isEmailClientField() {
      return this.sessionNoteField.type === 'clientField' && this.sessionNoteField.clientField === 'email';
    },

    allowMultiLines() {
      return (this.sessionNoteField.type === 'text' && this.sessionNoteField.allowMultiLines)
        || (this.sessionNoteField.type === 'clientField' && this.clientField?.allowMultiLines)
        || this.isAITextField;
    },

    allowMultipleOptions() {
      return (this.sessionNoteField.type === 'select' && this.sessionNoteField.allowMultipleOptions)
        || (this.sessionNoteField.type === 'clientField' && this.clientField?.allowMultipleOptions);
    },

    formattedSelectOptions() {
      const isClientFiledOptions = this.sessionNoteField.type === 'clientField';
      const options = isClientFiledOptions
        ? this.clientField?.options
        : this.sessionNoteField.options;

      return map(options, (option) => ({
        value: AppUtils.isPlainObject(option) ? option.value : option,
        label: AppUtils.isPlainObject(option) ? option.label : option,
      }));
    }, // formatSelectOptions()

    formattedScoreRangeOptions() {
      if (this.sessionNoteField.scoreConfig?.scoreType === 'linearScale') {
        const min = this.sessionNoteField.scoreConfig?.scoreRange?.min?.value || 0;
        const max = this.sessionNoteField.scoreConfig?.scoreRange?.max?.value || 0;
        const step = this.sessionNoteField.scoreConfig?.step || 1;
        if (step <= 0) {
          return [];
        }
        const options = [];
        for (let i = min; i <= max; i += step) {
          options.push({
            value: i,
            label: i,
          });
        }
        return options;
      }
      return [];
    }, // formattedScoreRangeOptions()

    formattedExplicitScoreOptions() {
      return this.sessionNoteField.scoreConfig?.explicitValues || [];
    }, // formattedExplicitScoreOptions()

    warning() {
      if (isFunction(this.sessionNoteField.warning)) {
        return this.sessionNoteField.warning(
          this.sessionNoteField.value,
          this.session,
          this.sessionActivity,
          this.linkedAppointment,
        );
      }
      return undefined;
    },
  },

  async mounted() {
    if (!this.sessionNoteField.value) {
      this.refreshAIField();
    }
  }, // mounted()

  methods: {

    onInputDate(selectedDate) {
      this.$emit('input', selectedDate instanceof Timestamp
        ? this.adjustDate(selectedDate.toDate(), { hour: 12, minute: 0, second: 0, millisecond: 0 })
        : null);
    }, // onInputDate()

    onInputTime(selectedDate) {
      const adjustFunc = this.sessionNoteField.adjust;
      let adjustedDate = selectedDate;

      if (typeof adjustFunc === 'function') {
        adjustedDate = adjustFunc(
          selectedDate,
          {
            startTimestamp: this.getStartTimestamp(),
            sessionActivity: this.sessionActivity,
          },
        );
      }

      this.$emit('input', adjustedDate instanceof Timestamp
        ? adjustedDate.toDate()
        : null);
    }, // onInputTime()

    onSelectOption(selectedOptions) {
      this.$emit('select-option', selectedOptions);
    }, // onSelectOption()

    onSelectRadioOption(selectedOption) {
      this.$emit('select-single-option', selectedOption);
    }, // onSelectRadioOption()

    onInputValue(value) {
      if (this.sessionNoteField.type === 'number') {
        this.$emit('input', value === '' || value === null || value === undefined ? null : Number(value));
      } else {
        this.$emit('input', value);
      }
    }, // onInputValue()

    onContinue() {
      this.$emit('continue');
    }, // onContinue(

    onContinueFromTextField() {
      if (!this.allowMultiLines) {
        this.onContinue();
      }
    },

    onProcessing(value) {
      this.$emit('block-continue', value);
    }, // onProcessing()

    onRefreshAIField() {
      if (!this.isSubscriptionPremium) {
        this.alertDialog(this.$t('common.titleFeatureUnavailable'), this.$t('common.errors.aiPremiumFeature'));
        return;
      }
      this.refreshAIField();
    }, // onRefreshAIField()

    async refreshAIField() {
      if (this.sessionNoteField.type !== 'aiText' || this.offlineMode || !this.isSubscriptionPremium) {
        return;
      }

      this.isGeneratingText = true;

      const sessionData = map(this.sessionData, (sessionProgram) => {
        const program = find(this.programs, { id: sessionProgram.program });

        const supervisionData = chain(this.sessionDataNotes)
          .find((sessionDataNotes) => (
            sessionDataNotes.program === sessionProgram.program
          && sessionDataNotes.user === this.sessionActivity.user
          ))
          .pick(['supervisionNotes'])
          .value();

        return {
          programName: program?.programName,
          programTags: program?.tags,
          programNotes: sessionProgram.notes || null,
          ...supervisionData,
          targetData: chain(sessionProgram)
            .get('data')
            .groupBy('target')
            .map((data, targetId) => {
              const dataWithTrials = data.filter((d) => has(d, 'trial') && has(d, 'promptLevel'));
              return {
                targetName: get(sessionProgram.targetsLabels, targetId) || null,
                numObservations: size(dataWithTrials),
                result: isEmpty(dataWithTrials)
                  ? null
                  : sumBy(dataWithTrials, 'scoreValue') / (size(dataWithTrials) * 100),
                data: map(dataWithTrials, (dataItem) => (
                  chain(this.promptLevelTemplates)
                    .get(
                      [dataItem.promptLevelTemplate || sessionProgram.promptLevelTemplate,
                        'promptLevels',
                      ],
                    )
                    .find({ id: dataItem.promptLevel })
                    .get('trialOutcome')
                    .value()
                )),
                ...this.timedTargetMeasurements[sessionProgram.id]?.[targetId],
              };
            })
            .value(),
        };
      });

      const client = find(this.clients, { id: this.session?.client });
      let clientData = {};
      if (client) {
        clientData = {
          clientName: client.clientName,
          clientFirstName: client.firstName,
          clientLastName: client.lastName,
        };
        if (client.birthDate) {
          clientData.clientAge = calculateAge(client.birthDate);
        }
        if (client.gender) {
          clientData.clientGender = client.gender;
        }
      }
      try {
        const retVal = await callFirebaseFunction('aiGenerateText', {
          clinicId: this.userDetails.clinic,
          fieldInstructions: this.sessionNoteField.aiInstructions,
          clientData,
          sessionData,
          activityType: this.sessionActivity.type || 'session',
        });
        this.onInputValue(retVal.data);
      } catch (err) {
        this.notifyNegative(this.$t(aiErrorMessageKey(err, 'common.errors.generic')));
      } finally {
        this.isGeneratingText = false;
      }
    }, // refreshAIField()

    convertTimestamp(d) {
      return isDate(d) ? Timestamp.fromDate(d) : null;
    }, // convertTimestamp()

    formatSelectValue(value) {
      if (value) {
        return isArray(value) ? value : [value];
      }
      return [];
    }, // formatSelectValue()

    isValidField(value) {
      if (this.sessionNoteField.type === 'number') {
        return (!this.sessionNoteField.required) || (isNumber(value) && !isNaN(value));
      }

      if (this.isEmailClientField) {
        return (!this.sessionNoteField.required && isEmpty(value))
          || isValidEmail(value);
      }

      return (!this.sessionNoteField.required || !isEmpty(value));
    },

    getStartTimestamp() {
      const enteredStartTime = this.groupedSessionNoteFields.find(({ id, value }) => {
        return id === SESSION_NOTE_FIELDS.ENTERED_START_TIME && value;
      });
      if (enteredStartTime) {
        return this.convertTimestamp(enteredStartTime.value);
      }

      const sessionStartTime = this.groupedSessionNoteFields.find(({ id, value }) => {
        return id === SESSION_NOTE_FIELDS.SESSION_START_TIME && value;
      });
      if (sessionStartTime) {
        return this.convertTimestamp(sessionStartTime.value);
      }

      return this.sessionActivity.startTimestamp;
    },

  },
};
</script>
